'use client';

import { useEffect, useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';

// Persistier-Verzögerung beim Tippen. Bewusst entkoppelt vom Anzeigewert:
// die Eingabe ist sofort sichtbar (lokaler State), erst der Server-Commit ist
// gebündelt — analog zu useDebouncedInput der Kopf-Felder. So feuert NICHT
// jeder Tastendruck eine Mutation (die im Backend alle Positionen neu anlegt).
const COMMIT_DELAY_MS = 500;

interface QuantityStepperProps {
  /** Extern persistierte Anzahl (Server/Refetch). */
  value: number;
  /** Commit nach außen. `0` ist erlaubt — der Aufrufer entfernt dann die Position. */
  onChange: (next: number) => void;
  containerClassName?: string;
  inputClassName?: string;
  buttonClassName?: string;
}

/**
 * Anzahl-Stepper für die Materialanfrage-Positionen.
 *
 * Hält den Eingabewert lokal (String, damit Leeren/Editieren funktioniert) und
 * persistiert debounced. Verhindert damit den Fokus-/Tippverlust, der entstand,
 * als jeder Tastendruck direkt `updateQuantity` (async Mutation + Refetch mit
 * neuen Item-IDs) auslöste.
 */
export function QuantityStepper({
  value,
  onChange,
  containerClassName,
  inputClassName,
  buttonClassName,
}: QuantityStepperProps) {
  const [draft, setDraft] = useState(() => String(value));
  const lastSyncedRef = useRef(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Externer Wert geändert (Refetch, Kundenwechsel, Reset) → lokal übernehmen
  // und laufenden Commit verwerfen.
  useEffect(() => {
    if (value !== lastSyncedRef.current) {
      lastSyncedRef.current = value;
      clearTimer();
      setDraft(String(value));
    }
  }, [value]);

  // Timer beim Unmount aufräumen.
  useEffect(() => () => clearTimer(), []);

  const commitNow = (next: number) => {
    clearTimer();
    lastSyncedRef.current = next;
    onChange(next);
  };

  const scheduleCommit = (next: number) => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      lastSyncedRef.current = next;
      onChange(next);
    }, COMMIT_DELAY_MS);
  };

  const currentNumeric = () => {
    const parsed = parseInt(draft, 10);
    return Number.isFinite(parsed) ? parsed : value;
  };

  const step = (delta: number) => {
    const next = Math.max(0, currentNumeric() + delta);
    setDraft(String(next));
    // Buttons gebündelt persistieren — schnelle Klickfolgen ergeben einen Commit.
    scheduleCommit(next);
  };

  const handleChange = (raw: string) => {
    const cleaned = raw.replace(/\D/g, '');
    setDraft(cleaned);
    const parsed = parseInt(cleaned, 10);
    // Leeres/ungültiges Feld vorübergehend erlauben, aber nicht committen.
    if (Number.isFinite(parsed) && parsed >= 1) scheduleCommit(parsed);
  };

  const handleBlur = () => {
    const parsed = parseInt(draft, 10);
    if (!Number.isFinite(parsed) || parsed < 1) {
      // Ungültig/leer → auf letzten gültigen Wert zurücksetzen.
      clearTimer();
      setDraft(String(value));
      return;
    }
    if (parsed !== lastSyncedRef.current) commitNow(parsed);
  };

  return (
    <div className={containerClassName}>
      <button
        type="button"
        aria-label="Anzahl verringern"
        onClick={() => step(-1)}
        className={buttonClassName}
      >
        <Minus size={14} />
      </button>
      <input
        type="number"
        value={draft}
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
        className={inputClassName}
        aria-label="Anzahl"
      />
      <button
        type="button"
        aria-label="Anzahl erhöhen"
        onClick={() => step(1)}
        className={buttonClassName}
      >
        <Plus size={14} />
      </button>
    </div>
  );
}
